import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableTeamSelect } from './SearchableTeamSelect';
import { Label } from '@/components/ui/label';
import { useGetFeedLeaguesQuery, useGetFeedTeamsQuery } from '@/api/feed';
import {
  generateTeamLogoUrl,
  generatePlayerJerseyUrl,
  generateTeamImageUrl,
  generateTeamJerseyImageUrl,
  NCAAMB_MANUAL_ASSETS,
  type Sport,
} from '@/utils/jersey-mapping';

const JERSEY_NUMBERS = Array.from({ length: 100 }, (_, i) => i);

/** Special asset IDs for NCAA placeholder team images (ncaamb/teams/) */
const NCAA_PLACEHOLDER_AWAY = '__AWAY-NCAAMB__';
const NCAA_PLACEHOLDER_HOME = '__HOME-NCAAMB__';

/** NBA team abbreviation → full display name (city + nickname) for admin dropdowns */
const NBA_FULL_NAMES: Record<string, string> = {
  ATL: 'Atlanta Hawks',
  BOS: 'Boston Celtics',
  BKN: 'Brooklyn Nets',
  CHA: 'Charlotte Hornets',
  CHI: 'Chicago Bulls',
  CLE: 'Cleveland Cavaliers',
  DAL: 'Dallas Mavericks',
  DEN: 'Denver Nuggets',
  DET: 'Detroit Pistons',
  GSW: 'Golden State Warriors',
  HOU: 'Houston Rockets',
  IND: 'Indiana Pacers',
  LAC: 'LA Clippers',
  LAL: 'Los Angeles Lakers',
  MEM: 'Memphis Grizzlies',
  MIA: 'Miami Heat',
  MIL: 'Milwaukee Bucks',
  MIN: 'Minnesota Timberwolves',
  NOP: 'New Orleans Pelicans',
  NYK: 'New York Knicks',
  OKC: 'Oklahoma City Thunder',
  ORL: 'Orlando Magic',
  PHI: 'Philadelphia 76ers',
  PHX: 'Phoenix Suns',
  POR: 'Portland Trail Blazers',
  SAC: 'Sacramento Kings',
  SAS: 'San Antonio Spurs',
  TOR: 'Toronto Raptors',
  UTA: 'Utah Jazz',
  WAS: 'Washington Wizards',
  WSH: 'Washington Wizards',
};

type DirectoryType = 'jerseys' | 'teams' | 'team-jerseys';

function getTeamDisplayName(
  team: { name: string; abbreviation?: string },
  sportId: string
): string {
  const abbr = team.abbreviation?.toUpperCase();
  if (sportId?.toLowerCase() === 'nba' && abbr && NBA_FULL_NAMES[abbr]) {
    return NBA_FULL_NAMES[abbr];
  }
  return team.name;
}

interface JerseySelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (url: string) => void;
  fieldLabel?: string;
  restrictedLeagueId?: string;
}

export function JerseySelectorModal({
  open,
  onOpenChange,
  onSelect,
  fieldLabel = 'Image',
  restrictedLeagueId,
}: JerseySelectorModalProps) {
  const [directoryType, setDirectoryType] = useState<DirectoryType>('jerseys');
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [selectedNumber, setSelectedNumber] = useState<number | 'blank'>('blank');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [imageError, setImageError] = useState(false);

  // Fetch data from feed service
  const { data: leagues = [], isLoading: isLoadingLeagues } = useGetFeedLeaguesQuery(undefined);
  const { data: feedTeams = [], isLoading: isLoadingTeams } =
    useGetFeedTeamsQuery(selectedLeagueId);

  // For NCAAMB, merge manual assets (e.g. Arbys-HOME, Arbys-AWAY) with feed teams
  const teams =
    selectedLeagueId === 'ncaamb'
      ? [...NCAAMB_MANUAL_ASSETS.map(a => ({ ...a, market: undefined })), ...feedTeams]
      : feedTeams;

  useEffect(() => {
    if (open && restrictedLeagueId && !selectedLeagueId) {
      setSelectedLeagueId(restrictedLeagueId);
    }
  }, [open, restrictedLeagueId, selectedLeagueId]);

  // Reset downstream selections when league or directory type changes
  useEffect(() => {
    setSelectedTeamId('');
    setSelectedNumber('blank');
  }, [selectedLeagueId, directoryType]);

  // Reset number when team changes
  useEffect(() => {
    setSelectedNumber('blank');
  }, [selectedTeamId]);

  // Find selected entities
  const selectedLeague = leagues.find(l => l.externalId === selectedLeagueId);
  const selectedTeam = teams.find(t => t.externalId === selectedTeamId);
  const isNcaambPlaceholder =
    selectedTeamId === NCAA_PLACEHOLDER_AWAY || selectedTeamId === NCAA_PLACEHOLDER_HOME;

  // Generate preview URL
  useEffect(() => {
    const sport = selectedLeague?.sportId as Sport | undefined;
    if (!sport) {
      setPreviewUrl('');
      return;
    }

    // NCAA placeholder assets (ncaamb/teams/AWAY-NCAAMB.png, HOME-NCAAMB.png)
    if (directoryType === 'teams' && isNcaambPlaceholder) {
      const assetName = selectedTeamId === NCAA_PLACEHOLDER_AWAY ? 'AWAY-NCAAMB' : 'HOME-NCAAMB';
      setPreviewUrl(generateTeamImageUrl(assetName, 'NCAAMB'));
      setImageError(false);
      return;
    }

    if (selectedTeam && selectedLeague) {
      let url: string;

      if (directoryType === 'teams') {
        // Teams directory: one .svg/.png file per team (no number needed)
        url = generateTeamImageUrl(selectedTeam.abbreviation || '', sport);
      } else if (directoryType === 'team-jerseys') {
        // team-jerseys directory: one .png file per team (no number needed)
        url = generateTeamJerseyImageUrl(selectedTeam.abbreviation || '', sport);
      } else {
        // Jerseys directory
        if (selectedNumber === 'blank') {
          // Team logo
          url = generateTeamLogoUrl(selectedTeam.abbreviation || '', sport);
        } else {
          // Player jersey with number
          url = generatePlayerJerseyUrl(selectedTeam.abbreviation || '', selectedNumber, sport);
        }
      }

      setPreviewUrl(url);
      setImageError(false);
    } else {
      setPreviewUrl('');
    }
  }, [
    selectedTeam,
    selectedLeague,
    selectedNumber,
    directoryType,
    selectedTeamId,
    isNcaambPlaceholder,
  ]);

  const handleSelect = () => {
    if (previewUrl) {
      onSelect(previewUrl);
      handleClose();
    }
  };

  const handleClose = () => {
    setDirectoryType('jerseys');
    setSelectedLeagueId('');
    setSelectedTeamId('');
    setSelectedNumber('blank');
    setPreviewUrl('');
    setImageError(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Select Image {fieldLabel && `for ${fieldLabel}`}</DialogTitle>
          <DialogDescription>
            Choose a directory type, league, and team. Jerseys allow optional jersey numbers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="directory-type-select">Directory Type</Label>
            <Select
              value={directoryType}
              onValueChange={value => setDirectoryType(value as DirectoryType)}
            >
              <SelectTrigger id="directory-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="jerseys">Jerseys</SelectItem>
                <SelectItem value="teams">Teams</SelectItem>
                <SelectItem value="team-jerseys">Team Jerseys</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="league-select">League</Label>
            <Select
              value={selectedLeagueId}
              onValueChange={setSelectedLeagueId}
              disabled={isLoadingLeagues || !!restrictedLeagueId}
            >
              <SelectTrigger id="league-select">
                <SelectValue
                  placeholder={isLoadingLeagues ? 'Loading leagues...' : 'Select a league'}
                />
              </SelectTrigger>
              <SelectContent>
                {leagues.map(league => (
                  <SelectItem key={league.externalId} value={league.externalId}>
                    {league.name} ({league.sportId})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedLeagueId && (
            <div className="space-y-2">
              <Label htmlFor="team-select">Team</Label>
              <SearchableTeamSelect
                teams={teams}
                value={selectedTeamId}
                onValueChange={setSelectedTeamId}
                disabled={isLoadingTeams}
                placeholder={isLoadingTeams ? 'Loading teams...' : 'Select a team'}
                id="team-select"
                getDisplayName={team => getTeamDisplayName(team, selectedLeague?.sportId ?? '')}
                leadingOptions={
                  directoryType === 'teams' && selectedLeague?.sportId?.toLowerCase() === 'ncaamb'
                    ? [
                        { value: NCAA_PLACEHOLDER_AWAY, label: 'Away (AWAY-NCAAMB)' },
                        { value: NCAA_PLACEHOLDER_HOME, label: 'Home (HOME-NCAAMB)' },
                      ]
                    : undefined
                }
              />
            </div>
          )}

          {selectedTeamId && directoryType === 'jerseys' && (
            <div className="space-y-2">
              <Label htmlFor="number-select">Jersey Number (Optional)</Label>
              <Select
                value={selectedNumber.toString()}
                onValueChange={value =>
                  setSelectedNumber(value === 'blank' ? 'blank' : parseInt(value, 10))
                }
              >
                <SelectTrigger id="number-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="blank">Team Logo (No Number)</SelectItem>
                  {JERSEY_NUMBERS.map(num => (
                    <SelectItem key={num} value={num.toString()}>
                      #{num.toString().padStart(2, '0')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {previewUrl && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="relative w-full h-64 border rounded-md overflow-hidden bg-gray-50">
                {!imageError ? (
                  <img
                    src={previewUrl}
                    alt={
                      directoryType === 'teams'
                        ? 'Team'
                        : directoryType === 'team-jerseys'
                          ? 'Team jersey'
                          : 'Jersey'
                    }
                    className="w-full h-full object-contain"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="flex w-full h-full items-center justify-center text-sm text-muted-foreground">
                    Failed to load{' '}
                    {directoryType === 'teams'
                      ? 'team'
                      : directoryType === 'team-jerseys'
                        ? 'team jersey'
                        : 'jersey'}{' '}
                    image
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground break-all">{previewUrl}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSelect} disabled={!previewUrl || imageError}>
            Select
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
